/**
 * @fileoverview Single Discover timeline clock (WEB-779 / PAR-05).
 *
 * Start-face, countdown, group assignment, and the NOW marker all derive from
 * one ISO instant and one IANA zone. Formatting goes through `Intl` with an
 * ASCII-space meridiem so ICU narrow-no-break-space variants cannot invent a
 * second clock. Instant math (`Date.parse`) is timezone-independent — the
 * zone is only for the human-readable face.
 *
 * @since WEB-779
 */

const MS_PER_MINUTE = 60_000;

export interface SpineClockParts {
  /** 12-hour clock face including minutes, e.g. `8:04`. */
  readonly clock: string;
  readonly period: 'AM' | 'PM' | '';
  /** 0–23 hour in `timeZone`, used for LATE classification. */
  readonly hour24: number;
  /** `8:04 PM` — the same face the NOW marker prints. */
  readonly label: string;
}

const EMPTY_CLOCK_PARTS: SpineClockParts = {
  clock: '',
  period: '',
  hour24: Number.NaN,
  label: '',
};

const timePartsFormatterCache = new Map<string, Intl.DateTimeFormat>();

function getCachedFormatter(
  cache: Map<string, Intl.DateTimeFormat>,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const cached = cache.get(timeZone);
  if (cached) return cached;
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat('en-US', { ...options, timeZone });
  } catch {
    formatter = new Intl.DateTimeFormat('en-US', { ...options, timeZone: 'UTC' });
  }
  cache.set(timeZone, formatter);
  return formatter;
}

/**
 * Parse an event (or clock) instant to epoch ms. `Date.parse` is the one
 * comparison the timeline uses — do not re-derive "local now" from a zone.
 */
export function parseEventInstantMs(instant: string | Date): number {
  if (instant instanceof Date) return instant.getTime();
  return Date.parse(instant);
}

function periodFromDayPeriod(raw: string): 'AM' | 'PM' | '' {
  const upper = raw.toUpperCase();
  if (upper.includes('PM')) return 'PM';
  if (upper.includes('AM')) return 'AM';
  return '';
}

function hour24FromParts(hour12: number, period: 'AM' | 'PM' | ''): number {
  if (!Number.isFinite(hour12)) return Number.NaN;
  if (period === 'AM') return hour12 === 12 ? 0 : hour12;
  if (period === 'PM') return hour12 === 12 ? 12 : hour12 + 12;
  return hour12;
}

/**
 * Split an instant into the spine clock face in `timeZone`.
 *
 * Assembled from `formatToParts` so the meridiem separator is always a
 * plain ASCII space — some ICU builds emit U+202F / U+00A0 from `format()`.
 */
export function formatSpineClockParts(
  instant: string | Date,
  timeZone: string,
): SpineClockParts {
  const ms = parseEventInstantMs(instant);
  if (Number.isNaN(ms)) return EMPTY_CLOCK_PARTS;
  const date = instant instanceof Date ? instant : new Date(ms);

  const parts = getCachedFormatter(timePartsFormatterCache, timeZone, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);

  const hourRaw = parts.find((part) => part.type === 'hour')?.value ?? '';
  const minute = parts.find((part) => part.type === 'minute')?.value ?? '00';
  const period = periodFromDayPeriod(
    parts.find((part) => part.type === 'dayPeriod')?.value ?? '',
  );
  const hour12 = Number.parseInt(hourRaw, 10);
  if (!Number.isFinite(hour12)) return EMPTY_CLOCK_PARTS;
  const hour24 = hour24FromParts(hour12, period);
  const clock = `${hour12}:${minute}`;
  const label = period ? `${clock} ${period}` : clock;
  return { clock, period, hour24, label };
}

/** `8:04 PM` in `timeZone` — NOW marker and city-clock time fragment. */
export function formatSpineNowLabel(instant: Date, timeZone: string): string {
  return formatSpineClockParts(instant, timeZone).label;
}

/** Whole minutes until `startsAt`; negative once started. `NaN` if unparseable. */
export function minutesUntilStart(startsAt: string, nowMs: number): number {
  const startMs = parseEventInstantMs(startsAt);
  if (Number.isNaN(startMs)) return Number.NaN;
  return Math.floor((startMs - nowMs) / MS_PER_MINUTE);
}

/** True when `startsAt` is parseable and `nowMs >= startsAt`. */
export function isStarted(startsAt: string, nowMs: number): boolean {
  const startMs = parseEventInstantMs(startsAt);
  if (Number.isNaN(startMs)) return false;
  return nowMs >= startMs;
}
